import { emitShellPendingSyncChanged } from '@core/domain/sessionEvents.ts';
import { getErrorMessage } from '@core/domain/utils/errorHandler.ts';
import { probeAppNetworkReachable } from '@core/bootstrap/factories/networkReachabilityProbe.ts';
import { isPublicShellPath } from '@core/bootstrap/shellBootstrap.ts';
import type { AuthApplicationPort } from '@src/modules/auth/domain/ports/AuthApplicationPort.ts';
import type { NotificationPort } from '@src/modules/platform/notifications/domain/ports/NotificationPort.ts';
import type { SystemApplicationService } from '@src/modules/system/application/SystemApplicationService.ts';
import { createConnectivitySyncOrchestrator } from '@src/modules/system/application/connectivitySyncOrchestrator.ts';
import {
  hideSyncPushOverlay,
  showSyncPushOverlay,
  updateSyncPushOverlay,
} from '@src/modules/shell-ui/infrastructure/shellSyncOverlay.ts';

export function installConnectivitySyncOrchestrator(deps: {
  auth: AuthApplicationPort;
  system: SystemApplicationService;
  notifications: NotificationPort;
  isCatalogSyncBusy: () => boolean;
}): () => void {
  const orchestrator = createConnectivitySyncOrchestrator({
    isSessionActive: () => deps.auth.isAuthenticated(),
    isRemoteSyncAvailable: async () => (await deps.system.getStatus()).remoteAvailable,
    isCatalogSyncBusy: () => deps.isCatalogSyncBusy(),
    isPublicShellPath: () => isPublicShellPath(globalThis.location.pathname),
    countPendingRecords: async () => {
      const pending = await deps.system.loadSyncPending();
      return pending.entregas.length + pending.reposiciones.length;
    },
    probeNetworkReachable: () => probeAppNetworkReachable(),
    pushPendingRecords: async () => {
      showSyncPushOverlay({ processed: 0, total: 0 });
      try {
        const result = await deps.system.pushNow({
          onProgress: (progress) => updateSyncPushOverlay(progress),
        });
        emitShellPendingSyncChanged();
        return { pushedTotal: result.pushedEntregas + result.pushedReposiciones };
      } finally {
        hideSyncPushOverlay();
      }
    },
    promptPush: (count, onSend) => {
      const label = count === 1 ? '1 registro' : `${count} registros`;
      deps.notifications.show(
        `Conexión detectada. Tiene ${label} por enviar.`,
        'info',
        12_000,
        { label: 'Enviar', onClick: onSend },
      );
    },
    notifyPushSuccess: (pushedTotal) => {
      const label = pushedTotal === 1 ? '1 registro' : `${pushedTotal} registros`;
      deps.notifications.success(`Se enviaron ${label} al reconectar.`);
    },
    notifyPushError: (message) => {
      deps.notifications.error(getErrorMessage(message));
    },
  });

  const onOnline = (): void => orchestrator.handleConnectivitySignal();
  globalThis.addEventListener('online', onOnline);

  return () => {
    globalThis.removeEventListener('online', onOnline);
    orchestrator.dispose();
  };
}
